// Maps the Employee infringement-diff reads onto the diff viewer's own model.
import moment from 'moment';

import { buildStatusTooltip } from 'pages/internal-admin/rules-engine';
import { comparisonModes } from './diffViewerQuery';

const isoDateFormat = "YYYY-MM-DD";
const agencyTypeName = "Agency";
const coreTypeName = "Core";

const defaultArray = [];

export const differenceSides = {
    value: "value",
    legacyOnly: "legacyOnly",
    underTestOnly: "underTestOnly"
};

export const sizeBuckets = {
    none: "none",
    exactHour: "exactHour",
    underMinute: "underMinute",
    underHour: "underHour",
    overHour: "overHour"
};

const sidesByWireValue = {
    ValueDifference: differenceSides.value,
    LegacyOnly: differenceSides.legacyOnly,
    UnderTestOnly: differenceSides.underTestOnly
};

const bucketsByWireValue = {
    NONE: sizeBuckets.none,
    EXACT_HOUR: sizeBuckets.exactHour,
    SUB_MINUTE: sizeBuckets.underMinute,
    UNDER_HOUR: sizeBuckets.underHour,
    OVER_HOUR: sizeBuckets.overHour
};

const modesByWireValue = {
    LegacyVersusShadow: comparisonModes.legacyVersusShadow,
    UnderTest: comparisonModes.underTest
};

function toIsoDate(value) {
    if (value == null) {
        return null;
    }

    return moment.utc(value).format(isoDateFormat);
}

// The endpoint's periods end on the instant they exclude; the viewer names the last date they include.
function toInclusiveEndDate(exclusiveEnd) {
    if (exclusiveEnd == null) {
        return null;
    }

    return moment.utc(exclusiveEnd).subtract(1, 'seconds').format(isoDateFormat);
}

function mapMode(wireMode) {
    return modesByWireValue[wireMode] ?? null;
}

function mapPeriod(period) {
    return {
        from: toIsoDate(period?.from),
        to: toInclusiveEndDate(period?.to)
    };
}

// In UnderTest the driver figures come back null, because coverage only measures legacy versus RE2. The
// provisional count is part of the still-open figure, not added to it.
function mapGate(gate) {
    if (gate == null) {
        return null;
    }

    return {
        stillOpen: gate.failureCount ?? null,
        stillOpenProvisional: gate.provisionalCount ?? null,
        analysedDrivers: gate.analysedDrivers ?? null,
        notAnalysedEnvironmental: gate.notAnalysedEnvironmental ?? null,
        notAnalysedGenuine: gate.notAnalysedGenuine ?? null,
        waiting: gate.waiting ?? null
    };
}

function mapAutoLabel(autoLabel) {
    if (autoLabel == null) {
        return null;
    }

    return {
        id: autoLabel.labelId,
        text: autoLabel.labelText,
        rootCause: autoLabel.rootCause ?? null,
        source: autoLabel.source?.sourceText ?? null
    };
}

function mapSignOff(signOff) {
    if (signOff == null) {
        return null;
    }

    return {
        labelText: signOff.labelText,
        reason: signOff.reason ?? null,
        workItemId: signOff.workItemId ?? null,
        source: signOff.source?.sourceText ?? null,
        signedOffBy: signOff.actorDisplayName ?? null,
        signedOffByUserId: signOff.actorUserId ?? null,
        signedOffAt: signOff.signedOffDate ?? null
    };
}

function buildDriverStatusTooltip(driver) {
    if (driver?.status == null) {
        return null;
    }

    return buildStatusTooltip({
        status: { name: driver.status },
        type: { name: driver.isAgency === true ? agencyTypeName : coreTypeName }
    });
}

// Shaped exactly as the harness's own driver search selects a driver, so opening a difference in the
// harness lands on the same driver the operator could have searched for.
function mapHarnessDriver(row) {
    const driver = row.driver;

    return {
        employeeId: driver?.employeeId ?? row.employeeId,
        accountId: driver?.accountId ?? null,
        name: driver?.name ?? null,
        reference: driver?.reference ?? null,
        cardNumber: driver?.cardNumber ?? null,
        accountName: driver?.accountName ?? null,
        memberNumber: driver?.memberNumber ?? null,
        locationName: driver?.location ?? null,
        statusName: driver?.status ?? null,
        isAgency: driver?.isAgency === true,
        statusTooltip: buildDriverStatusTooltip(driver)
    };
}

// Rows carry no mode of their own; every row in a read is of the mode the read was made for.
function mapDifference(row, mode) {
    return {
        id: row.infringementDiffId,
        mode: mode,
        side: sidesByWireValue[row.differenceSide] ?? row.differenceSide,
        signature: row.differenceSignature,
        typeId: row.infringementTypeId ?? null,
        code: row.code,
        subCode: row.subCode,
        fieldName: row.fieldName,
        occurredAt: row.occurredAt,
        legacyValue: row.legacyValue,
        underTestValue: row.underTestValue,
        secondsDifference: row.secondsDifference ?? null,
        counterpartOccurredAt: row.counterpartOccurredAt ?? null,
        isProvisional: row.isProvisional === true,
        lastEvaluatedWindowStart: row.lastEvaluatedWindowStartDate ?? null,
        lastEvaluatedRunDate: row.lastEvaluatedRunDate ?? null,
        driver: mapHarnessDriver(row),
        autoLabel: mapAutoLabel(row.autoLabel),
        signOff: mapSignOff(row.signOff)
    };
}

function mapGroup(group, mode) {
    return {
        signature: group.differenceSignature,
        mode: mode,
        typeId: group.infringementTypeId ?? null,
        code: group.code,
        subCode: group.subCode,
        fieldName: group.fieldName,
        side: sidesByWireValue[group.differenceSide] ?? group.differenceSide,
        sizeBucket: bucketsByWireValue[group.gapBucket] ?? group.gapBucket,
        count: group.count ?? 0
    };
}

export function mapDifferencesResponse(response) {
    const mode = mapMode(response?.comparisonMode);
    const period = mapPeriod(response?.period);

    return {
        mode: mode,
        from: period.from,
        to: period.to,
        whitelistedCount: response?.whitelistedCount ?? 0,
        gate: mapGate(response?.gate),
        differences: (response?.differences ?? defaultArray).map((row) => mapDifference(row, mode))
    };
}

export function mapGroupsResponse(response) {
    const mode = mapMode(response?.comparisonMode);

    return (response?.groups ?? defaultArray).map((group) => mapGroup(group, mode));
}

function buildTypeOptions(groups) {
    const options = [];
    const seenTypeIds = new Set();

    groups.forEach((group) => {
        if (group.infringementTypeId != null && !seenTypeIds.has(group.infringementTypeId)) {
            seenTypeIds.add(group.infringementTypeId);
            options.push({ id: group.infringementTypeId, code: group.code });
        }
    });

    return options;
}

/**
 * The facts shown beside the differences: the exit gate, how many whitelisted rows the
 * filters hide, and the infringement types to filter by.
 */
export function mapDiffViewerSummary(response) {
    const differences = response?.differences;

    return {
        gate: mapGate(differences?.gate),
        whitelistedCount: differences?.whitelistedCount ?? 0,
        typeOptions: buildTypeOptions(response?.typeGroups?.groups ?? defaultArray)
    };
}

export function mapCoverageResponse(response) {
    return {
        attempted: response?.driversAttempted ?? null,
        analysed: response?.driversAnalysed ?? null,
        analysedWithDifferences: response?.driversWithDifferences ?? null,
        notAnalysedEnvironmental: response?.driversNotAnalysedEnvironmental ?? null,
        notAnalysedGenuine: response?.driversNotAnalysedGenuine ?? null,
        superseded: response?.driversOnlySuperseded ?? null,
        waiting: response?.driversStillWaiting ?? null
    };
}

// The rows and the gate are passed through exactly as the endpoint returned them, so the file can be
// replayed against the read it came from. Rows carry no mode, so the read's mode and period go alongside them.
export function buildRawDifferencesExport(response, filters) {
    return {
        filters: filters,
        comparisonMode: response?.comparisonMode ?? null,
        period: response?.period ?? null,
        gate: response?.gate ?? null,
        differences: response?.differences ?? defaultArray
    };
}
